import React from 'react';
import { Lock, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
const MODE_REQUIRED_FLAG = {
    auto_store: 'allowAutoStore',
    feedback: 'allowFeedbackCampaigns',
    showroom: 'allowShowroom',
};
const MODE_LOCK_REASON = {
    auto_store: 'mode_auto_store',
    feedback: 'mode_feedback',
    showroom: 'mode_showroom',
};
export function TemplateCard({ template, proUnlocked, planFlags, onPick, onDelete }) {
    const { t } = useTranslation('campaign');
    const requiredFlag = MODE_REQUIRED_FLAG[template.mode];
    const modeLocked = requiredFlag !== undefined && !planFlags[requiredFlag];
    const proLocked = template.proOnly && !proUnlocked;
    const locked = proLocked || modeLocked;
    // Mode lock takes priority — if the user can't use the mode at all,
    // saying "Pro template" misses the point. Drop to "Pro template" only
    // when the mode itself is fine but the curated tier isn't.
    const lockReason = modeLocked
        ? MODE_LOCK_REASON[template.mode] ?? 'pro_template'
        : proLocked
            ? 'pro_template'
            : null;
    const isBlank = template.id === 'blank';
    const isWorkspace = template.source === 'workspace';
    return (<div className={cn('group relative flex flex-col overflow-hidden rounded-xl border bg-background text-left transition-all', 'focus-within:ring-2 focus-within:ring-primary', locked
            ? 'opacity-70 border-border'
            : 'border-border hover:border-primary/60 hover:shadow-md', isBlank && 'border-dashed')}>
            {locked && lockReason && (<Tooltip>
                    <TooltipTrigger asChild>
                        <span className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border bg-muted/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                            <Lock className="h-2.5 w-2.5"/>
                            {t('mode_picker.lock_badge')}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{t(`template_gallery.lock_tooltip_${lockReason}`)}</p>
                    </TooltipContent>
                </Tooltip>)}

            {isWorkspace && !locked && (<span className="absolute left-2 top-2 z-10 inline-flex items-center rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary-foreground backdrop-blur-sm">
                    {t('template_gallery.yours_badge')}
                </span>)}

            {onDelete && (<button type="button" onClick={(e) => {
                e.stopPropagation();
                onDelete();
            }} aria-label={t('template_gallery.delete_aria')} className="absolute right-2 top-2 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground hover:border-destructive hover:text-destructive backdrop-blur-sm">
                    <Trash2 className="h-3 w-3"/>
                </button>)}

            <button type="button" onClick={() => !locked && onPick(template)} disabled={locked} aria-disabled={locked} className={cn('flex flex-1 flex-col text-left', 'focus-visible:outline-none', locked ? 'cursor-not-allowed' : 'cursor-pointer')}>
                {template.previewImage && (<div className="aspect-[5/3] w-full overflow-hidden bg-muted">
                        <img src={template.previewImage} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105"/>
                    </div>)}

                <div className="flex flex-1 flex-col gap-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold leading-tight">{template.name}</h3>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {template.mode.replace('_', ' ')}
                        </span>
                    </div>
                    {template.description && (<p className="text-xs text-muted-foreground leading-snug line-clamp-3">{template.description}</p>)}
                </div>
            </button>
        </div>);
}
